import { useEffect, useState } from 'react'

import { FixedPictureFieldModel, LocalFile, RemoteFile } from '@types'
import { useTranslation } from 'react-i18next'

import { useFilePreviews } from '@hooks/file/file-preview.hook'

import { UploadPicture } from '@components/UploadPicture'
import { Input } from '@components/Input/Input.component'

import { FormFieldCard, FormFieldCardProps } from '../FormFieldCard'

type ActionsProps = Pick<FormFieldCardProps, 'canDown' | 'canUp' | 'onDelete' | 'onDuplicate' | 'onUp' | 'onDown'>

type FormFieldFixedPictureProps = {
  field: FixedPictureFieldModel
  onChange: (value: Partial<FixedPictureFieldModel>) => void
  onLocalFilesChange?: (files: LocalFile[]) => void
} & ActionsProps

export const FormFieldFixedPicture: React.FC<FormFieldFixedPictureProps> = ({
  field,
  onChange,
  onLocalFilesChange,
  ...actionsProps
}: FormFieldFixedPictureProps) => {
  const { t } = useTranslation()

  const [files, setFiles] = useState<(RemoteFile | LocalFile)[]>([])

  const { loadFilePreviews } = useFilePreviews({
    files: field.files as RemoteFile[],
    onFilesLoaded: (files) => (files ? setFiles(files as RemoteFile[]) : undefined),
  })

  useEffect(() => {
    if (field.files) {
      loadFilePreviews()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilesChange = (newFiles: (LocalFile | RemoteFile)[]) => {
    const local = newFiles.filter((file): file is LocalFile => Boolean((file as LocalFile).data))
    const remote = newFiles.filter((file): file is RemoteFile => !Boolean((file as LocalFile).data))
    const undeletedRemoteFiles = remote.filter((file) => !file.deleted)

    onChange({
      ids: undeletedRemoteFiles.map(({ id }) => id),
    })

    onLocalFilesChange?.(local)
    setFiles(newFiles)
  }

  return (
    <FormFieldCard name={field.label} hideRequired onChangeRequired={() => {}} {...actionsProps}>
      <Input
        type="text"
        required
        label={t('modules_project-form-models_components_FormFieldFixedPicture_question_label')}
        value={field.label}
        onChange={(value) => onChange({ label: value })}
      />

      <UploadPicture
        label={t('modules_project-form-models_components_FormFieldFixedPicture_label')}
        required
        multiple
        files={files}
        setFiles={handleFilesChange}
      />
    </FormFieldCard>
  )
}
